import React, { useEffect, useState, useContext } from 'react';
import { ContextAPI } from '../../../../ContextAPI';
import AddNumberPlateTransferModal from './AddNumberPlateTransferModal';
import {
  Table,
  Input,
  Dropdown,
  Typography,
  Button,
  Icon,
  Menu,
  Tag,
  Popover,
} from "antd";
import { platformApi } from '../../../../api';

const { Search } = Input;
const { Item } = Menu;
const { Title } = Typography;
import moment from 'moment';

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20];

const displayValue = (value) => {
  if (value == null || value === "") return "-";
  return value;
};

const renderRegNoPopover = (plate = {}) => {
  const phones = Array.isArray(plate.customerPhones)
    ? plate.customerPhones.filter(Boolean)
    : [];
  const modelCode = (plate.modelCode || "").trim();
  const modelName = (plate.modelName || "").trim();
  const vehicleLabel =
    modelCode && modelName
      ? `${modelCode} - ${modelName}`
      : modelCode || modelName || "-";

  const rowStyle = {
    display: "grid",
    gridTemplateColumns: "128px 1fr",
    columnGap: 12,
    rowGap: 2,
    marginBottom: 10,
    alignItems: "start",
    lineHeight: 1.5,
  };
  const labelStyle = {
    color: "#434343",
    fontSize: 12,
    fontWeight: 600,
  };
  const valueStyle = {
    color: "#262626",
    fontSize: 13,
    fontWeight: 600,
    wordBreak: "break-word",
  };

  return (
    <div
      style={{
        minWidth: 280,
        maxWidth: 360,
        padding: "10px 12px",
        background: "linear-gradient(180deg, #fafafa 0%, #f0f0f0 100%)",
        borderRadius: 8,
        border: "1px solid #e8e8e8",
      }}
    >
      <div style={rowStyle}>
        <span style={labelStyle}>Booking ID</span>
        <span style={valueStyle}>{displayValue(plate.bookingId)}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Customer Name</span>
        <span style={valueStyle}>{displayValue(plate.customerName)}</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Customer Mobile</span>
        <span style={valueStyle}>
          {phones.length === 0
            ? "-"
            : phones.map((phone, idx) => (
                <div key={`${phone}-${idx}`} style={{ marginBottom: idx === phones.length - 1 ? 0 : 4 }}>
                  {phone}
                </div>
              ))}
        </span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>Vehicle</span>
        <span style={valueStyle}>{vehicleLabel}</span>
      </div>
      <div style={{ ...rowStyle, marginBottom: 0 }}>
        <span style={labelStyle}>Chassis No</span>
        <span style={valueStyle}>{displayValue(plate.chassisNo)}</span>
      </div>
    </div>
  );
};

const groupPendingSuggestions = (suggestions = []) => {
  const groupedMap = {};
  suggestions.forEach((rec) => {
    const fromId = rec.fromBranch?.id;
    const toId = rec.toBranch?.id;
    if (!fromId || !toId || !rec.numberPlate?.id) return;
    const key = `${fromId}_${toId}`;
    if (!groupedMap[key]) {
      groupedMap[key] = {
        fromBranchId: fromId,
        fromBranchName: rec.fromBranch?.name || "Source Branch",
        toBranchId: toId,
        toBranchName: rec.toBranch?.name || "Target Branch",
        plates: [],
        totalCount: 0,
      };
    }
    groupedMap[key].plates.push(rec.numberPlate);
    groupedMap[key].totalCount += 1;
  });
  return Object.values(groupedMap);
};

const NumberPlates = () => {

  const [open, setOpen] = useState(false);
  const [createAccess, setCreateAccess] = useState(false);
  const { loginCredintials } = useContext(ContextAPI);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [autoTransferData, setAutoTransferData] = useState(null);

  const handlePageSizeChange = (size) => {
    setLimit(size);
    setPage(1);
    getNumberPlates(1, size, searchString);
  };

  const handleSearch = (value) => {
    setSearchString(value);
    setPage(1);
    getNumberPlates(1, limit, value);
  };

  const getMenu = () => {
    return PAGE_SIZE_OPTIONS.map((menu) => (
      <Item onClick={() => handlePageSizeChange(menu)} key={menu}>
        {menu}
      </Item>
    ));
  };

  const menu = <Menu>{getMenu()}</Menu>;

  const getPendingTransfers = () => {
    platformApi
      .get("/api/inventoryTransfers/pendingNumberPlateTransfers")
      .then((res) => {
        if (res?.data?.code === 200) {
          setPendingTransfers(groupPendingSuggestions(res.data.data || []));
        }
      })
      .catch((err) => console.error("Error fetching pending number plate transfers:", err));
  };

  useEffect(() => {
    getNumberPlates(page, limit, searchString);
    getPendingTransfers();
    const handlePendingUpdate = () => getPendingTransfers();
    window.addEventListener("pendingNumberPlateTransfersUpdated", handlePendingUpdate);
    return () => {
      window.removeEventListener("pendingNumberPlateTransfersUpdated", handlePendingUpdate);
    };
  }, []);

  useEffect(() => {
    if (loginCredintials && loginCredintials.roleAccess) {
      loginCredintials.roleAccess.forEach((each) => {
        if (each.subModule === "transfer_Number_Plates") {
          if (each.access.create) {
            setCreateAccess(true);
          }
        }
      });
    }
  }, [loginCredintials]);

  const getNumberPlates = async (pg = page, lm = limit, search = searchString) => {
    try {
      setLoading(true);
      const res = await platformApi.post(`/api/inventoryTransfers/numberPlate-transfers`, {
        page: pg,
        size: lm,
        searchString: search || undefined
      });

      setData(res.data.data || []);
      setCount(res.data.count || 0);
    } catch (error) {
      console.error("Error fetching transfers:", error);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setAutoTransferData(null);
    getNumberPlates(1, limit, searchString);
    getPendingTransfers();
  };

  const columns = [
    {
      title: "S.No",
      key: "sno",
      width: 70,
      render: (text, record, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      width: 150,
      render: (createdAt) => {
        if (!createdAt) return "-";
        return moment(createdAt).format("DD/MM/YYYY");
      },
    },
    {
      title: "From Branch",
      render: (_, record) => record.fromBranch?.name || "-",
    },
    {
      title: "To Branch",
      render: (_, record) => record.toBranch?.name || "-",
    },
    {
      title: "Model Name",
      dataIndex: "numberPlate.modelName",
      render: (_, record) => record.numberPlate?.modelName || "-",
    },
    {
      title: "Register No",
      render: (_, record) => record.numberPlate?.registerNo || "-",
    },
    {
      title: "Chassis No",
      render: (_, record) => record.numberPlate?.chassisNo || "-",
    },
  ];

  return (
    <div>
      <style>{`
        .np-regno-popover .ant-popover-inner {
          background: transparent;
          box-shadow: 0 6px 16px rgba(0,0,0,0.12);
          border-radius: 8px;
        }
        .np-regno-popover .ant-popover-inner-content {
          padding: 0;
        }
        .np-regno-popover .ant-popover-arrow::before,
        .np-regno-popover .ant-popover-arrow-content {
          background: #f5f5f5 !important;
        }
      `}</style>
      {pendingTransfers.length > 0 && (
        <div
          style={{
            background: "#fff2f0",
            border: "1px solid #ffccc7",
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
            boxShadow: "0 2px 8px rgba(255, 77, 79, 0.12)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18, color: "#ff4d4f" }}>⚡</span>
              <h4 style={{ margin: 0, color: "#cf1322", fontWeight: 700, fontSize: 16 }}>
                Pending Number Plate Transfers ({pendingTransfers.length} Action Needed)
              </h4>
            </div>
            <span style={{ color: "#8c8c8c", fontSize: 13 }}>
              Number plates received at a branch different from the vehicle booking branch.
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {pendingTransfers.map((rec, idx) => (
              <div
                key={`${rec.fromBranchId}_${rec.toBranchId}_${idx}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#ffffff",
                  border: "1px solid #ffd591",
                  borderRadius: 6,
                  padding: "12px 16px",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
                  <div>
                    <span style={{ fontSize: 12, color: "#8c8c8c", display: "block" }}>From (Received Branch)</span>
                    <span style={{ color: "#389e0d", fontWeight: 600, fontSize: 14 }}>{rec.fromBranchName}</span>
                  </div>
                  <div style={{ fontSize: 16, color: "#1890ff", fontWeight: "bold" }}>➔</div>
                  <div>
                    <span style={{ fontSize: 12, color: "#8c8c8c", display: "block" }}>To (Booking Branch)</span>
                    <span style={{ color: "#096dd9", fontWeight: 600, fontSize: 14 }}>{rec.toBranchName}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: "#8c8c8c", display: "block" }}>Register Nos</span>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                      {rec.plates.map((plate) => (
                        <Popover
                          key={plate.id}
                          content={renderRegNoPopover(plate)}
                          trigger="hover"
                          placement="top"
                          mouseEnterDelay={0.25}
                          mouseLeaveDelay={0.1}
                          overlayStyle={{ padding: 0 }}
                          overlayClassName="np-regno-popover"
                        >
                          <span style={{ display: "inline-block", cursor: "pointer" }}>
                            <Tag
                              color="blue"
                              style={{ fontSize: 12, margin: 0 }}
                            >
                              {plate.registerNo || plate.chassisNo || plate.id}
                            </Tag>
                          </span>
                        </Popover>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: "#8c8c8c", display: "block" }}>Total Plates</span>
                    <Tag color="red" style={{ fontWeight: "bold", fontSize: 13, padding: "2px 8px" }}>
                      {rec.totalCount} Plate{rec.totalCount > 1 ? "s" : ""}
                    </Tag>
                  </div>
                </div>
                <Button
                  type="danger"
                  style={{
                    backgroundColor: "#ff4d4f",
                    borderColor: "#ff4d4f",
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: 4,
                  }}
                  onClick={() => {
                    setAutoTransferData(rec);
                    setOpen(true);
                  }}
                  disabled={!createAccess}
                >
                  ⚡ Auto Transfer ({rec.totalCount} Plate{rec.totalCount > 1 ? "s" : ""})
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HEADER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Number Plate Transfer [{count}]
          <span style={{ marginLeft: 12 }}>
            <Dropdown overlay={menu} trigger={["click"]}>
              <Button style={{ minWidth: 50 }}>
                {limit} <Icon type="down" />
              </Button>
            </Dropdown>
          </span>
        </Title>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Search
            style={{ width: 220 }}
            placeholder="Search Transfers"
            onSearch={handleSearch}
            onChange={(e) => {
              if (e.target.value === "") {
                handleSearch("");
              }
            }}
            allowClear
            size="default"
          />
          <Button
            type="primary"
            style={{ minWidth: 180 }}
            onClick={() => {
              setAutoTransferData(null);
              setOpen(true);
            }}
            disabled={!createAccess}
          >
            Add Number Plates Transfer
          </Button>
        </div>
      </div>

      {/* TABLE */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: limit,
          total: count,
          showSizeChanger: false,
          onChange: (pg) => {
            setPage(pg);
            getNumberPlates(pg, limit, searchString);
          },
        }}
      />

      {/* ADD TRANSFER MODAL */}
      {open && (
        <AddNumberPlateTransferModal
          open={open}
          close={closeModal}
          createAccess={createAccess}
          initialValues={autoTransferData}
        />
      )}
    </div>
  );
};

export default NumberPlates;
